// packages/web/src/pages/PlayerProfilePage.tsx - Mobile Responsive Version
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  SelectChangeEvent,
  Tooltip,
  Switch,
  FormControlLabel,
  FormGroup,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SportsIcon from '@mui/icons-material/Sports';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import MessageIcon from '@mui/icons-material/Message';
import { addDays, format, addMinutes, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek, isToday } from 'date-fns';
import { useBannerState } from '../hooks/useBannerState';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { TennisRatingSlider } from '../components/Common/TennisRatingSelector';
import { CourtSearchSelect } from '../components/Common/CourtSearchSelect';
import { PlayerSlot, Court, Challenge, PlayerSlotService, PlayerRatings, formatPlayerRatings, EmailPreferences } from '@matchpoint/shared';
import { formatTimeRange, formatDate } from '@matchpoint/shared';
import { useAuth } from '../hooks/useAuth';
// Import necessary services
import { CourtService, ChallengeService, FirebaseService, UserProfileService } from '@matchpoint/shared';

import PersonIcon from '@mui/icons-material/Person';
import { AuthService } from '@matchpoint/shared';
import GoogleIcon from '@mui/icons-material/Google';
import { Link, useSearchParams } from 'react-router-dom';

// Import chat types and services
import { Chat, Message, ChatService } from '@matchpoint/shared';
import { ChatList } from '../components/Chat/ChatList';
import { ChatView } from '../components/Chat/ChatView';

const profileTabs = [
  { label: "My Schedule", index: 0 },
  { label: "Received Challenges", index: 1 },
  { label: "Sent Challenges", index: 2 },
  { label: "Messages", index: 3 },
  { label: "Account Settings", index: 4 },
];

const ProfileContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, // Critical for flex children
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  fontSize: '2.5rem',
  border: '4px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#84b817',
  [theme.breakpoints.down('md')]: {
    width: 80,
    height: 80,
    fontSize: '2rem',
    border: '3px solid rgba(255, 255, 255, 0.5)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
    fontSize: '1.5rem',
    border: '3px solid rgba(255, 255, 255, 0.5)',
  },
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'auto', // Changed from 'hidden' to 'auto' to allow scrolling
  minHeight: 0,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
  },
}));

const ProfileSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  minHeight: 0, // Critical for flex children
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
}));

const SlotCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: '12px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderColor: '#c4d600',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
    borderRadius: '8px',
  },
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  height: '700px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    height: 'auto',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
  },
}));

const CalendarSection = styled(Paper)(({ theme }) => ({
  flex: '0 0 auto',
  width: 'auto',
  padding: theme.spacing(2),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    maxWidth: '100%',
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25),
    flex: '0 0 auto',
    maxWidth: '450px',
    margin: '0 auto',
    width: '100%',
  },
}));

const DayDetailsSection = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  overflowY: 'auto',
  maxHeight: '600px',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    maxHeight: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25),
  },
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.25),
    marginTop: theme.spacing(1),
  },
}));

const CalendarDay = styled(Box, {
  shouldForwardProp: (prop) => !['isSelected', 'isToday', 'hasSlots', 'isCurrentMonth'].includes(prop as string),
})<{ isSelected?: boolean; isToday?: boolean; hasSlots?: boolean; isCurrentMonth?: boolean }>(
  ({ theme, isSelected, isToday, hasSlots, isCurrentMonth }) => ({
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '8px',
    border: '1px solid transparent',
    position: 'relative',
    backgroundColor: isSelected ? '#c4d600' : isToday ? '#f0f8e8' : '#ffffff',
    color: isSelected ? '#1a1a1a' : isCurrentMonth ? '#1a1a1a' : '#999',
    fontWeight: isToday || isSelected ? 700 : 400,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: isSelected ? '#b3c200' : '#fafafa',
      borderColor: '#c4d600',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '4px',
    },
  })
);

const SlotIndicator = styled(Box)(({ theme }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: '#84b817',
  position: 'absolute',
  bottom: 4,
  [theme.breakpoints.down('sm')]: {
    width: 4,
    height: 4,
    bottom: 2,
  },
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));

const WeekDayHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: '#666',
  padding: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: theme.spacing(0.25),
  },
}));

const ChallengeItem = styled(ListItem)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(0.75),
    padding: theme.spacing(1),
  },
}));

// Messages-specific styled components
const MessagesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  minHeight: 0,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    border: 'none',
    borderRadius: 0,
  },
}));

const ChatListContainer = styled(Box)(({ theme }) => ({
  flex: '0 0 350px',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 280px',
  },
  [theme.breakpoints.down('sm')]: {
    flex: '1',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    maxHeight: '40%',
    height: '100%',
  },
}));

const ChatViewContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    flex: '1',
    minHeight: 0,
    height: '100%',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: any;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, sx, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`player-profile-tabpanel-${index}`}
      aria-labelledby={`player-profile-tab-${index}`}
      style={{ 
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}
      {...other}
    >
      {value === index && (
        <TabPanel sx={sx}>{children}</TabPanel>
      )}
    </div>
  );
}

export const PlayerProfilePage: React.FC = () => {
  console.log('🔍 PlayerProfilePage component rendered');
  const { user, signOut } = useAuth();
  console.log('🔍 useAuth returned user:', {
    hasUser: !!user,
    uid: user?.uid,
    email: user?.email,
    name: user?.name,
    hasPreferences: !!user?.preferences,
    hasEmailPreferences: !!user?.preferences?.emailPreferences
  });
  
  // Read tab parameter from URL (if present)
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  
  // Map tab names to indices
  const getTabIndex = (tabName: string | null): number => {
    if (!tabName) return 0;
    const tabMap: Record<string, number> = {
      'schedule': 0,
      'received': 1,
      'sent': 2,
      'messages': 3,
      'settings': 4
    };
    return tabMap[tabName] ?? 0;
  };
  
  const [tabValue, setTabValue] = useState(getTabIndex(urlTab));
  const [playerSlots, setPlayerSlots] = useState<PlayerSlot[]>([]);
  const [sentChallenges, setSentChallenges] = useState<Challenge[]>([]);
  const [receivedChallenges, setReceivedChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const playerSlotService = PlayerSlotService.getInstance();
  const { onSlotCreated } = useBannerState(user?.uid);

  // NEW: Chat state
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [mobileShowChat, setMobileShowChat] = useState(false); // Track mobile chat view
  const chatService = ChatService.getInstance();
  
  // Local user state for immediate UI updates
  const [localUser, setLocalUser] = useState(user);
  
  // Calendar states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  
  // Dialog states
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<PlayerSlot | null>(null);
  const [slotDialogError, setSlotDialogError] = useState<string | null>(null);
  const [shareDialogSuccess, setShareDialogSuccess] = useState<string | null>(null);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [challengeToAccept, setChallengeToAccept] = useState<Challenge | null>(null);
  
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [challengeToCancel, setChallengeToCancel] = useState<Challenge | null>(null);
  const [cancellationMessage, setCancellationMessage] = useState('');

  // New slot form state
  const [slotForm, setSlotForm] = useState({
    startTime: new Date(),
    duration: 60,
    courtId: '',
    courtData: null as Court | null,
    visibility: 'public' as 'public' | 'private'
  });
  
  // Account settings form state
  const [accountForm, setAccountForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    ratings: user?.preferences?.ratings || { utr: null, ntrp: null }
  });

  // Email preferences state - separate master toggle from individual preferences
  const [masterEmailToggle, setMasterEmailToggle] = useState<boolean>(
    user?.preferences?.emailNotifications !== false
  );
  const [emailPrefs, setEmailPrefs] = useState<EmailPreferences>({
    challengeReceived: user?.preferences?.emailPreferences?.challengeReceived ?? (user?.preferences?.emailNotifications !== false),
    challengeAccepted: user?.preferences?.emailPreferences?.challengeAccepted ?? (user?.preferences?.emailNotifications !== false),
    challengeRejected: user?.preferences?.emailPreferences?.challengeRejected ?? (user?.preferences?.emailNotifications !== false),
    accountSecurity: true
  });
  
  // Update local user when auth user changes
  useEffect(() => {
    console.log('🔍 PlayerProfilePage: user changed, updating localUser');
    setLocalUser(user);
  }, [user]);

  // Update accountForm when user data changes
  useEffect(() => {
    console.log('🔍 PlayerProfilePage: updating account form based on user data');
    if (user) {
      setAccountForm({
        name: user.name,
        email: user.email,
        ratings: user.preferences?.ratings || { utr: null, ntrp: null }
      });

      // Initialize email preferences from user data
      const emailNotifications = user.preferences?.emailNotifications !== false;
      setMasterEmailToggle(emailNotifications);
      setEmailPrefs({
        challengeReceived: user.preferences?.emailPreferences?.challengeReceived ?? emailNotifications,
        challengeAccepted: user.preferences?.emailPreferences?.challengeAccepted ?? emailNotifications,
        challengeRejected: user.preferences?.emailPreferences?.challengeRejected ?? emailNotifications,
        accountSecurity: true
      });
    }
  }, [user]);

  // Listen for browser back/forward button clicks
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get('tab');
      const newTabIndex = getTabIndex(urlTab);
      setTabValue(newTabIndex);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getTabIndex]);

  useEffect(() => {
    const loadUserData = async () => {
      console.log('🔍 PlayerProfilePage: loadUserData called, user?.uid:', user?.uid);
      if (!user?.uid) {
        console.log('❌ PlayerProfilePage: No user UID, skipping data load');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 PlayerProfilePage: Starting to load user data for uid:', user.uid);
        
        // Load player slots
        console.log('🔍 PlayerProfilePage: Loading player slots...');
        const slotsData = await playerSlotService.getUserSlots(user.uid);
        console.log('✅ PlayerProfilePage: Player slots loaded:', slotsData.length);
        setPlayerSlots(slotsData);
        
        // Load challenges
        console.log('🔍 PlayerProfilePage: Loading challenges...');
        const challengeService = ChallengeService.getInstance();
        const [sent, received] = await Promise.all([
          challengeService.getSentChallenges(user.uid),
          challengeService.getReceivedChallenges(user.uid)
        ]);
        
        console.log('✅ PlayerProfilePage: Challenges loaded - sent:', sent.length, 'received:', received.length);
        setSentChallenges(sent);
        setReceivedChallenges(received);
        
        // Load chats
        console.log('🔍 PlayerProfilePage: Loading chats...');
        const userChats = await chatService.getUserChats(user.uid);
        console.log('✅ PlayerProfilePage: Chats loaded:', userChats.length);
        setChats(userChats);
        
      } catch (err) {
        console.error('❌ PlayerProfilePage: Error loading data:', err);
        
        if (err instanceof Error && err.message.includes('permission-denied')) {
          setError('Authentication error. Please sign in again.');
          setTimeout(() => {
            signOut();
          }, 2000);
        } else {
          setError('Failed to load data. Please try again.');
        }
      } finally {
        setLoading(false);
        console.log('✅ PlayerProfilePage: Data loading complete');
      }
    };
    
    loadUserData();
  }, [user]); 

  // Reload only player slots
  const loadPlayerSlots = async () => {
    if (!user) return;
    
    try {
      const slotsData = await playerSlotService.getUserSlots(user.uid);
      // Sort slots by start time
      const sortedSlots = slotsData.sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      setPlayerSlots(sortedSlots);
    } catch (err) {
      console.error('Error loading player slots:', err);
      setError('Failed to load slots. Please try again.');
    }
  };

  // NEW: Load user chats
  const loadUserChats = async () => {
    if (!user) return;
    
    try {
      const userChats = await chatService.getUserChats(user.uid);
      setChats(userChats);
      
      // If we had a selected chat, try to find it in the updated list
      if (selectedChat) {
        const updatedSelectedChat = userChats.find(chat => chat.id === selectedChat.id);
        if (updatedSelectedChat) {
          setSelectedChat(updatedSelectedChat);
        }
      }
    } catch (err) {
      console.error('Error loading chats:', err);
      throw err;
    }
  };

  // Full data load for initial load and challenge operations
  const loadUserData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Load player slots (sorted)
      await loadPlayerSlots();
      
      // Load challenges
      const challengeService = ChallengeService.getInstance();
      const [sent, received] = await Promise.all([
        challengeService.getSentChallenges(user.uid),
        challengeService.getReceivedChallenges(user.uid)
      ]);
      
      setSentChallenges(sent);
      setReceivedChallenges(received);
      
      // NEW: Load chats
      await loadUserChats();
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Update URL to reflect the tab change
    const tabMap: Record<number, string> = {
      0: 'schedule',
      1: 'received',
      2: 'sent',
      3: 'messages',
      4: 'settings'
    };
    
    const tabName = tabMap[newValue];
    if (tabName) {
      // Update URL without reloading the page
      window.history.pushState({}, '', `/profile?tab=${tabName}`);
    }
  };

  const handleOpenSlotDialog = (slot?: PlayerSlot) => {
    // Check if slot has an associated challenge
    if (slot && slot.challengeId) {
      // Find the challenge to check its status
      const challenge = [...receivedChallenges, ...sentChallenges].find(c => c.id === slot.challengeId);
      if (challenge && (challenge.status === 'accepted' || challenge.status === 'pending')) {
        setError('Cannot edit a slot that has an accepted or pending challenge');
        return;
      }
    }
    
    if (slot) {
      setEditingSlot(slot);
      setSlotForm({
        startTime: new Date(slot.startTime),
        duration: slot.duration,
        courtId: slot.courtId === 'any' ? '' : slot.courtId,
        courtData: null,
        visibility: slot.visibility || 'public'
      });
    } else {
      setEditingSlot(null);
      // If we have a selected date, use it for the new slot
      const baseDate = selectedDate || new Date();
      const newStartTime = new Date(baseDate);
      // Set default time to 10:00 AM
      newStartTime.setHours(10, 0, 0, 0);
      
      setSlotForm({
        startTime: newStartTime,
        duration: 60,
        courtId: '',
        courtData: null,
        visibility: 'public'
      });
    }
    
    setSlotDialogOpen(true);
  };

  const handleCloseSlotDialog = () => {
    setSlotDialogOpen(false);
    setEditingSlot(null);
    setSlotDialogError(null);
    setSlotForm({
      startTime: new Date(),
      duration: 60,
      courtId: '',
      courtData: null,
      visibility: 'public'
    });
  };

  const handleSaveSlot = async () => {
    if (!user) {
      setSlotDialogError('You must be logged in to create slots');
      return;
    }
    
    // Clear any previous dialog errors
    setSlotDialogError(null);
    
    try {
      let courtName = 'Any Court';
      let courtId = 'any';
      let city = 'Any City';
      let country = '';
      
      // If a specific court is selected and we have court data from Algolia
      if (slotForm.courtId && slotForm.courtId !== '') {
        if (slotForm.courtData) {
          courtId = slotForm.courtData.id || (slotForm.courtData as any).objectID || slotForm.courtId;
          courtName = slotForm.courtData.name;
          city = slotForm.courtData.location.city;
          // Extract country from location - Algolia stores postal code in country field
          country = slotForm.courtData.details?.region || 
                   (isNaN(Number(slotForm.courtData.location.country)) ? slotForm.courtData.location.country : '') || '';
        } else {
          // Fallback: fetch court details from CourtService if courtData is not available
          const courtService = CourtService.getInstance();
          try {
            const court = await courtService.getCourt(slotForm.courtId);
            if (court) {
              courtId = court.id;
              courtName = court.name;
              city = court.location.city;
              country = court.location.country || '';
            }
          } catch (error) {
            console.error('Error fetching court details:', error);
            // Use the court ID but show warning
            courtId = slotForm.courtId;
            courtName = 'Court';
            city = 'Unknown City';
            country = '';
          }
        }
      }
      
      if (editingSlot) {
        // Update existing slot
        const slotData: Partial<PlayerSlot> = {
          startTime: slotForm.startTime,
          duration: slotForm.duration,
          courtId: courtId,
          court: courtName,
          city: city,
          country: country,
          visibility: slotForm.visibility,
          timezone: user.preferences?.timezone || 'UTC',
          updatedAt: new Date()
        };
        
        await playerSlotService.updatePlayerSlot(editingSlot.id, slotData);
        setSuccess('Slot updated successfully');
      } else {
        // Create new slot
        await playerSlotService.createPlayerSlot(
          user,
          slotForm.startTime,
          slotForm.duration,
          courtId,
          courtName,
          city,
          country,
          slotForm.visibility
        );
        setSuccess('Slot created successfully');
        onSlotCreated();
      }
      
      handleCloseSlotDialog();
      await loadPlayerSlots();
    } catch (err) {
      console.error('Error saving slot:', err);
      // Show the actual error message from the service in the dialog
      const errorMessage = err instanceof Error ? err.message : 'Failed to save slot. Please try again.';
      setSlotDialogError(errorMessage);
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    // Find the slot to check if it has a challenge
    const slot = playerSlots.find(s => s.id === slotId);
    if (slot && slot.challengeId) {
      // Find the challenge to check its status
      const challenge = [...receivedChallenges, ...sentChallenges].find(c => c.id === slot.challengeId);
      if (challenge && (challenge.status === 'accepted' || challenge.status === 'pending')) {
        setError('Cannot delete a slot that has an accepted or pending challenge');
        return;
      }
    }
    
    if (!window.confirm('Are you sure you want to delete this slot?')) {
      return;
    }
    
    try {
      await playerSlotService.deletePlayerSlot(slotId);
      setSuccess('Slot deleted successfully');
      await loadPlayerSlots();
    } catch (err) {
      console.error('Error deleting slot:', err);
      setError('Failed to delete slot. Please try again.');
    }
  };

  const validateUserBeforeAction = (): boolean => {
    if (!user?.uid) {
      setError('Authentication required. Please sign in again.');
      setTimeout(() => signOut(), 1000);
      return false;
    }
    return true;
  };

  const handleAcceptChallenge = async (challengeId: string) => {
    if (!validateUserBeforeAction()) return;
    
    // Find the challenge to show confirmation dialog
    const challenge = receivedChallenges.find(c => c.id === challengeId);
    if (challenge) {
      setChallengeToAccept(challenge);
      setAcceptDialogOpen(true);
    }
  };

  const handleConfirmAcceptChallenge = async () => {
    if (!challengeToAccept) return;
    
    try {
      console.log('🔍 PlayerProfilePage: Accepting challenge:', challengeToAccept.id);
      const challengeService = ChallengeService.getInstance();
      await challengeService.acceptChallenge(challengeToAccept.id);
      console.log('✅ PlayerProfilePage: Challenge accepted successfully');
      setSuccess('Challenge accepted! The match is now confirmed. A chat has been created for coordination. Other overlapping challenges have been automatically withdrawn.');
      
      // Reload all data to refresh the UI including new chats
      await loadUserData();
      
      // Close the dialog
      setAcceptDialogOpen(false);
      setChallengeToAccept(null);
    } catch (err) {
      console.error('❌ PlayerProfilePage: Error accepting challenge:', err);
      setError('Failed to accept challenge. Please try again.');
      setAcceptDialogOpen(false);
      setChallengeToAccept(null);
    }
  };

  const handleCancelAcceptChallenge = () => {
    setAcceptDialogOpen(false);
    setChallengeToAccept(null);
  };

  const handleRejectChallenge = async (challengeId: string) => {
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService.rejectChallenge(challengeId);
      setSuccess('Challenge rejected');
      await loadUserData();
    } catch (err) {
      console.error('Error rejecting challenge:', err);
      setError('Failed to reject challenge. Please try again.');
    }
  };

  const handleWithdrawChallenge = async (challengeId: string) => {
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService.withdrawChallenge(challengeId);
      setSuccess('Challenge withdrawn successfully');
      await loadUserData();
    } catch (err) {
      console.error('Error withdrawing challenge:', err);
      setError('Failed to withdraw challenge. Please try again.');
    }
  };

  const handleOpenShareDialog = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareDialogSuccess(null);
  };

  const handleCopyShareLink = () => {
    const shareLink = `${window.location.origin}/players/${user?.uid}/schedule`;
    navigator.clipboard.writeText(shareLink);
    setShareDialogSuccess('Share link copied to clipboard!');
  };

  const handleAccountUpdate = async () => {
    if (!user) return;
    
    try {
      console.log('📝 Step 1: Updating user document...');
      
      // Use UserProfileService to update user profile
      const userProfileService = UserProfileService.getInstance();
      await userProfileService.updateNameAndRatings(
        user.uid,
        accountForm.name,
        accountForm.ratings
      );
      console.log('✅ Step 1 complete: User document updated');
      
      console.log('📝 Step 2: Updating player slots ratings...');
      await playerSlotService.updateUserSlotsRatings(
        user.uid, 
        accountForm.ratings,
        user.preferences?.timezone
      );
      console.log('✅ Step 2 complete: Player slots updated');

      console.log('📝 Step 3: Updating challenge ratings...');
      const challengeService = ChallengeService.getInstance();
      await challengeService.updateUserChallengesRatings(
        user.uid,
        accountForm.ratings
      );
      console.log('✅ Step 3 complete: Challenges updated');

      setSuccess('Account updated successfully');
      
      // Update local user state immediately for UI
      if (localUser) {
        setLocalUser({
          ...localUser,
          preferences: {
            ...localUser.preferences,
            ratings: accountForm.ratings
          }
        });
      }
      
    } catch (err) {
      console.error('❌ Error updating account:', err);
      console.error('Error details:', {
        code: (err as any)?.code,
        message: (err as any)?.message,
        stack: (err as any)?.stack
      });
      setError('Failed to update account. Please try again.');
    }
  };

  const handleEmailPreferencesUpdate = async () => {
    if (!user?.uid) {
      setError('User not found. Please sign in again.');
      return;
    }
    
    try {
      const userProfileService = UserProfileService.getInstance();
      
      // Update email preferences using the service
      await userProfileService.updateEmailPreferences(user.uid, {
        challengeReceived: emailPrefs.challengeReceived,
        challengeAccepted: emailPrefs.challengeAccepted,
        challengeRejected: emailPrefs.challengeRejected,
        accountSecurity: emailPrefs.accountSecurity
      });
      
      setSuccess('Email preferences updated successfully');
      
      // Update local user state
      if (localUser) {
        setLocalUser({
          ...localUser,
          preferences: {
            ...localUser.preferences,
            emailNotifications: masterEmailToggle,
            emailPreferences: {
              challengeReceived: emailPrefs.challengeReceived,
              challengeAccepted: emailPrefs.challengeAccepted,
              challengeRejected: emailPrefs.challengeRejected,
              accountSecurity: emailPrefs.accountSecurity
            }
          }
        });
      }
      
    } catch (err) {
      console.error('Error updating email preferences:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('permission-denied')) {
          setError('Permission denied. Please sign in again.');
          setTimeout(() => signOut(), 2000);
        } else if (err.message.includes('network')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError('Failed to update email preferences. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleOpenCancelDialog = (challenge: Challenge) => {
    setChallengeToCancel(challenge);
    
    // Pre-populate with a polite cancellation message
    const defaultMessage = `I apologize, but I need to cancel our scheduled match on ${formatDate(challenge.startTime)} at ${formatTimeRange(challenge.startTime, challenge.endTime)}. I hope we can reschedule for another time.`;
    
    setCancellationMessage(defaultMessage);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancellation = async () => {
    if (!challengeToCancel || !user) return;
    
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService.cancelAcceptedChallenge(
        challengeToCancel.id,
        user.uid,
        cancellationMessage
      );
      
      setSuccess('Match cancelled. A cancellation message has been sent to your match partner.');
      
      // Reload data
      await loadUserData();
      
      // Close dialog
      setCancelDialogOpen(false);
      setChallengeToCancel(null);
      setCancellationMessage('');
    } catch (err) {
      console.error('Error cancelling challenge:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel match. Please try again.');
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setChallengeToCancel(null);
    setCancellationMessage('');
  };

  const handleEmailPreferenceChange = (preference: keyof EmailPreferences | 'master') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    
    if (preference === 'master') {
      // Master toggle - affects all other preferences and the master toggle
      setMasterEmailToggle(checked);
      setEmailPrefs({
        challengeReceived: checked,
        challengeAccepted: checked,
        challengeRejected: checked,
        accountSecurity: true
      });
    } else {
      // Individual preference change
      setEmailPrefs({
        ...emailPrefs,
        [preference]: checked
      });
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const authService = AuthService.getInstance();
      await authService.linkGoogleAccount();
      setSuccess('Google account connected successfully');
    } catch (err) {
      console.error('Error connecting Google account:', err);
      setError('Failed to connect Google account. It may already be linked to another account.');
    }
  };

  // NEW: Chat handlers
  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
    setMobileShowChat(true); // Show chat view on mobile
  };

  const handleBackToChats = () => {
    setMobileShowChat(false);
    // Optional: clear selection after animation
    setTimeout(() => setSelectedChat(null), 300);
  };

  // Helper function to get challenge names
  const getChallengerName = (challenge: Challenge) => {
    return challenge.fromUserName || 'Unknown';
  };

  const getChallengedName = (challenge: Challenge) => {
    return challenge.toUserName || 'Unknown';
  };

  // Helper function to get challenge status for a slot
  const getSlotChallengeStatus = (slot: PlayerSlot) => {
    if (!slot.challengeId) return null;
    
    const challenge = [...receivedChallenges, ...sentChallenges].find(c => c.id === slot.challengeId);
    return challenge?.status || null;
  };

  console.log('🔍 PlayerProfilePage: Rendering with loading:', loading, 'error:', error, 'user:', !!user);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header user={user} onSignOut={signOut} />
      
      <ProfileContainer maxWidth="lg" sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
      }}>
        <ProfileSection sx={{ 
          p: { xs: 0, sm: 3 },
          mb: { xs: 0, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 2,
            px: { xs: 1.5, sm: 0 }, // Add horizontal padding only on mobile
            pt: { xs: 1.5, sm: 0 }  // Add top padding only on mobile
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <ProfileAvatar src={localUser?.profilePicture}>
              {localUser?.name ? localUser.name.charAt(0).toUpperCase() : 'U'}
            </ProfileAvatar>
              <Box>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {localUser?.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {localUser?.email}
                </Typography>
                <Chip 
                  label={formatPlayerRatings(localUser?.preferences?.ratings || { utr: null, ntrp: null })} 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleOpenShareDialog}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Share
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, mx: { xs: 1.5, sm: 0 } }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3, mx: { xs: 1.5, sm: 0 } }}>
              {success}
            </Alert>
          )}
          
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: '#a1a1a1',
              }
            },
            // Show scroll indicator shadow on mobile
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)',
              pointerEvents: 'none',
              display: { xs: 'block', sm: 'none' }
            }
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: '120px' },
                  padding: { xs: '6px 8px', sm: '12px 16px' },
                },
                '& .MuiTabs-scrollButtons': {
                  color: 'primary.main',
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }
              }}
            >
              <Tab label="My Schedule" />
              <Tab 
                label="Received Challenges" 
                sx={{ '& .MuiBadge-badge': { right: -6, top: 3 } }}
              />
              <Tab 
                label="Sent Challenges" 
                sx={{ '& .MuiBadge-badge': { right: -6, top: 3 } }}
              />
              <Tab 
                label={`Messages (${chats.length})`}
                icon={<MessageIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                iconPosition="start"
              />
              <Tab label="Account Settings" />
            </Tabs>
          </Box>
          
          <CustomTabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
              <SectionTitle variant="h6">
                My Availability Schedule
              </SectionTitle>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <CalendarWrapper>
                {/* Calendar Section */}
                <CalendarSection elevation={1}>
                  <CalendarHeader>
                    <IconButton 
                      onClick={() => setCurrentMonth(addDays(currentMonth, -30))}
                      size="small"
                      sx={{ padding: { xs: '4px', sm: '8px' } }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {format(currentMonth, 'MMMM yyyy')}
                    </Typography>
                    <IconButton 
                      onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
                      size="small"
                      sx={{ padding: { xs: '4px', sm: '8px' } }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </CalendarHeader>
                  
                  {/* Week day headers */}
                  <CalendarGrid sx={{ marginBottom: 1 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                      <WeekDayHeader key={day}>
                        {/* Show single letter on mobile, abbreviation on larger screens */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{day}</Box>
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{day.charAt(0)}</Box>
                      </WeekDayHeader>
                    ))}
                  </CalendarGrid>
                  
                  {/* Calendar days */}
                  <CalendarGrid>
                    {(() => {
                      const monthStart = startOfMonth(currentMonth);
                      const monthEnd = endOfMonth(monthStart);
                      const startDate = startOfWeek(monthStart);
                      const endDate = endOfWeek(monthEnd);
                      const days = eachDayOfInterval({ start: startDate, end: endDate });
                      
                      return days.map(day => {
                        const daySlots = playerSlots.filter(slot => 
                          isSameDay(new Date(slot.startTime), day)
                        );
                        const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                        
                        return (
                          <CalendarDay
                            key={day.toISOString()}
                            isSelected={selectedDate ? isSameDay(day, selectedDate) : false}
                            isToday={isToday(day)}
                            hasSlots={daySlots.length > 0}
                            isCurrentMonth={isCurrentMonth}
                            onClick={() => setSelectedDate(day)}
                          >
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {format(day, 'd')}
                            </Typography>
                            {daySlots.length > 0 && <SlotIndicator />}
                          </CalendarDay>
                        );
                      });
                    })()}
                  </CalendarGrid>
                </CalendarSection>
                
                {/* Day Details Section */}
                <DayDetailsSection elevation={1}>
                  {selectedDate && (
                    <>
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {(() => {
                              const daySlots = playerSlots.filter(slot => 
                                isSameDay(new Date(slot.startTime), selectedDate)
                              );
                              return `${daySlots.length} slot${daySlots.length !== 1 ? 's' : ''} scheduled`;
                            })()}
                          </Typography>
                        </Box>
                        
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenSlotDialog()}
                          size="small"
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Add New Slot
                        </Button>
                      </Box>
                      
                      <Divider sx={{ mb: 3 }} />
                      
                      {(() => {
                        const daySlots = playerSlots
                          .filter(slot => isSameDay(new Date(slot.startTime), selectedDate))
                          .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
                        
                        if (daySlots.length === 0) {
                          return (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body1" color="text.secondary" gutterBottom>
                                No slots scheduled for this day
                              </Typography>
                            </Box>
                          );
                        }
                        
                        return daySlots.map((slot) => {
                          const challengeStatus = getSlotChallengeStatus(slot);
                          const isLocked = challengeStatus === 'accepted' || challengeStatus === 'pending';
                          
                          return (
                            <Box key={slot.id}>
                              <SlotCard>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {format(new Date(slot.startTime), 'h:mm a')} - {format(new Date(slot.startTime.getTime() + slot.duration * 60000), 'h:mm a')}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        {slot.duration} minutes
                                      </Typography>
                                    </Box>
                                    <Chip
                                      icon={slot.visibility === 'public' ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                                      label={slot.visibility === 'public' ? 'Public' : 'Private'}
                                      size="small"
                                      variant="outlined"
                                      color={slot.visibility === 'public' ? 'success' : 'default'}
                                      sx={{ height: 24, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LocationOnIcon fontSize="small" color="action" sx={{ fontSize: 18 }} />
                                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        {slot.court}, {slot.city}
                                        {slot.country ? `, ${slot.country}` : ''}
                                      </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', gap: 0.5, width: { xs: '100%', sm: 'auto' } }}>
                                      <Button
                                        size="small"
                                        onClick={() => handleOpenSlotDialog(slot)}
                                        startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                        sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.5, px: 1, flex: { xs: 1, sm: 'none' } }}
                                        disabled={isLocked}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteSlot(slot.id)}
                                        startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                        sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.5, px: 1, flex: { xs: 1, sm: 'none' } }}
                                        disabled={isLocked}
                                      >
                                        Delete
                                      </Button>
                                    </Box>
                                  </Box>
                                  
                                  {challengeStatus && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                      <CheckCircleIcon 
                                        fontSize="small" 
                                        color={challengeStatus === 'accepted' ? 'success' : 'primary'} 
                                        sx={{ fontSize: 16 }} 
                                      />
                                      <Typography 
                                        variant="caption" 
                                        color={challengeStatus === 'accepted' ? 'success.main' : 'primary'}
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                      >
                                        Challenge {challengeStatus}
                                      </Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </SlotCard>
                            </Box>
                          );
                        });
                      })()}
                    </>
                  )}
                </DayDetailsSection>
              </CalendarWrapper>
            )}
          </CustomTabPanel>
          
          <CustomTabPanel value={tabValue} index={1}>
            <SectionTitle variant="h6">
              Challenges Received
            </SectionTitle>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : receivedChallenges.length === 0 ? (
              <Typography variant="body1" sx={{ py: 4, textAlign: 'center' }}>
                You haven't received any challenges yet.
              </Typography>
            ) : (
              <List>
                {receivedChallenges.map((challenge) => (
                  <ChallengeItem key={challenge.id}>
                    <ListItemAvatar sx={{ minWidth: { xs: 40, sm: 56 } }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        {getChallengerName(challenge).charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {getChallengerName(challenge)}
                          </Typography>
                          <Chip
                            label={challenge.status}
                            size="small"
                            color={
                              challenge.status === 'accepted' ? 'success' :
                              challenge.status === 'rejected' ? 'error' :
                              challenge.status === 'withdrawn' ? 'default' :
                              'warning'
                            }
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        </Box>
                      }
                      secondary={
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <span style={{ fontSize: '0.75rem' }}>
                              {formatDate(challenge.startTime)} at {formatTimeRange(
                                challenge.startTime,
                                challenge.endTime
                              )}
                            </span>
                          </span>
                          
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LocationOnIcon fontSize="small" color="action" />
                            <span style={{ fontSize: '0.75rem' }}>
                              {challenge.courtName}
                            </span>
                          </span>
                          
                          <span style={{ display: 'block', marginTop: '8px' }}>
                            <span style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                              "{challenge.message}"
                            </span>
                          </span>
                        </span>
                      }
                    />
                    
                    {challenge.status === 'pending' && (
                      <ListItemSecondaryAction sx={{ right: { xs: 8, sm: 16 }, display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                        <IconButton 
                          edge="end" 
                          color="success"
                          onClick={() => handleAcceptChallenge(challenge.id)}
                          title="Accept"
                          size="small"
                        >
                          <CheckCircleIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          color="error"
                          onClick={() => handleRejectChallenge(challenge.id)}
                          title="Reject"
                          size="small"
                        >
                          <CancelIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}

                    {challenge.status === 'accepted' && (
                      <ListItemSecondaryAction sx={{ right: { xs: 8, sm: 16 } }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleOpenCancelDialog(challenge)}
                          startIcon={<CancelIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, padding: { xs: '2px 6px', sm: '4px 8px' } }}
                        >
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Cancel Match</Box>
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Cancel</Box>
                        </Button>
                      </ListItemSecondaryAction>
                    )}
                  </ChallengeItem>
                ))}
              </List>
            )}
          </CustomTabPanel>
          
          <CustomTabPanel value={tabValue} index={2}>
            <SectionTitle variant="h6">
              Challenges Sent
            </SectionTitle>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : sentChallenges.length === 0 ? (
              <Typography variant="body1" sx={{ py: 4, textAlign: 'center' }}>
                You haven't sent any challenges yet.
              </Typography>
            ) : (
              <List>
                {sentChallenges.map((challenge) => (
                  <ChallengeItem key={challenge.id}>
                    <ListItemAvatar sx={{ minWidth: { xs: 40, sm: 56 } }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        {getChallengedName(challenge).charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            To: {getChallengedName(challenge)}
                          </Typography>
                          <Chip
                            label={challenge.status}
                            size="small"
                            color={
                              challenge.status === 'accepted' ? 'success' :
                              challenge.status === 'rejected' ? 'error' :
                              challenge.status === 'withdrawn' ? 'default' :
                              'warning'
                            }
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        </Box>
                      }
                      secondary={
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <span style={{ fontSize: '0.75rem' }}>
                              {formatDate(challenge.startTime)} at {formatTimeRange(
                                challenge.startTime,
                                challenge.endTime
                              )}
                            </span>
                          </span>
                          
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LocationOnIcon fontSize="small" color="action" />
                            <span style={{ fontSize: '0.75rem' }}>
                              {challenge.courtName}
                            </span>
                          </span>
                          
                          <span style={{ display: 'block', marginTop: '8px' }}>
                            <span style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                              "{challenge.message}"
                            </span>
                          </span>
                        </span>
                      }
                    />
                    
                    {challenge.status === 'pending' && (
                      <ListItemSecondaryAction sx={{ right: { xs: 8, sm: 16 } }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleWithdrawChallenge(challenge.id)}
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, padding: { xs: '2px 6px', sm: '4px 8px' } }}
                        >
                          Withdraw
                        </Button>
                      </ListItemSecondaryAction>
                    )}

                    {challenge.status === 'accepted' && (
                      <ListItemSecondaryAction sx={{ right: { xs: 8, sm: 16 } }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleOpenCancelDialog(challenge)}
                          startIcon={<CancelIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, padding: { xs: '2px 6px', sm: '4px 8px' } }}
                        >
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Cancel Match</Box>
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Cancel</Box>
                        </Button>
                      </ListItemSecondaryAction>
                    )}
                  </ChallengeItem>
                ))}
              </List>
            )}
          </CustomTabPanel>
          
          {/* NEW: Messages Tab */}
          <CustomTabPanel value={tabValue} index={3} sx={{ 
            p: { xs: 0, sm: 3 },
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1, 
            minHeight: 0, 
            height: '100%' 
          }}>
            <SectionTitle variant="h6" sx={{ 
              mb: { xs: 1, sm: 2 }, 
              px: { xs: 1.5, sm: 0 }, 
              pt: { xs: 1.5, sm: 0 }, 
              flexShrink: 0,
              display: { xs: mobileShowChat ? 'none' : 'block', sm: 'block' }
            }}>
              Messages
            </SectionTitle>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <MessagesContainer sx={{ 
                mx: { xs: 0, sm: 3 }, 
                mb: { xs: 0, sm: 3 },
                mt: { xs: mobileShowChat ? 0 : 0, sm: 0 },
                flex: 1,
                minHeight: 0,
                height: '100%',
                flexDirection: { xs: mobileShowChat ? 'column' : 'column', sm: 'row' }
              }}>
                <ChatListContainer sx={{
                  display: { xs: mobileShowChat ? 'none' : 'flex', sm: 'flex' },
                }}>
                  <ChatList 
                    chats={chats}
                    selectedChatId={selectedChat?.id}
                    onChatSelect={handleChatSelect}
                    currentUserId={user?.uid || ''}
                  />
                </ChatListContainer>
                
                <ChatViewContainer sx={{
                  display: { xs: mobileShowChat ? 'flex' : 'none', sm: 'flex' }
                }}>
                  {selectedChat ? (
                    <ChatView 
                      chat={selectedChat}
                      currentUserId={user?.uid || ''}
                      currentUserName={user?.name || ''}
                      onBack={handleBackToChats}
                    />
                  ) : (
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: 'text.secondary'
                    }}>
                      <MessageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Select a conversation
                      </Typography>
                      <Typography variant="body2" textAlign="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Choose a chat from the list to start messaging with your match partner
                      </Typography>
                    </Box>
                  )}
                </ChatViewContainer>
              </MessagesContainer>
            )}
          </CustomTabPanel>
          
          <CustomTabPanel value={tabValue} index={4}>
            <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
              <SectionTitle variant="h6">
                Account Settings
              </SectionTitle>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                {/* Profile Information - Read Only */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Profile Information
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Name</Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>{user?.name}</Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Email</Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{user?.email}</Typography>
                  </Box>
                </Paper>

                {/* Tennis Ratings - Editable */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Tennis Ratings
                  </Typography>
                  
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <TennisRatingSlider
                      ratings={accountForm.ratings}
                      onChange={(ratings) => setAccountForm({ ...accountForm, ratings })}
                    />
                  </Box>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAccountUpdate}
                    fullWidth
                    sx={{ mt: 3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Save Changes
                  </Button>
                </Paper>

                {/* Email Notifications */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmailIcon color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Email Notifications
                    </Typography>
                  </Box>
                  
                  <FormGroup>
                    {/* Master toggle */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={masterEmailToggle}
                          onChange={handleEmailPreferenceChange('master')}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Email Notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Master setting for all email notifications
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Individual notification types */}
                    <Box sx={{ pl: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Individual Notification Types:
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={emailPrefs.challengeReceived || false}
                            onChange={handleEmailPreferenceChange('challengeReceived')}
                            disabled={!masterEmailToggle}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>🎾 New Challenge Received</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              When someone challenges you to a match
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={emailPrefs.challengeAccepted || false}
                            onChange={handleEmailPreferenceChange('challengeAccepted')}
                            disabled={!masterEmailToggle}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>✅ Challenge Accepted</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              When someone accepts your challenge
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={emailPrefs.challengeRejected || false}
                            onChange={handleEmailPreferenceChange('challengeRejected')}
                            disabled={!masterEmailToggle}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>❌ Challenge Rejected</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              When someone rejects your challenge
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={emailPrefs.accountSecurity || false}
                            disabled={true}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>🔒 Account Security</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              Password resets, login alerts (always enabled)
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </FormGroup>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEmailPreferencesUpdate}
                    fullWidth
                    sx={{ mt: 3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    startIcon={<NotificationsIcon />}
                  >
                    Save Email Preferences
                  </Button>
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <strong>Note:</strong> Critical security emails like password resets cannot be disabled for your account safety.
                    </Typography>
                  </Alert>
                </Paper>

                {/* Linked Accounts */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Linked Accounts
                  </Typography>
              
                  {!user?.linkedAccounts?.some(acc => acc.providerId === 'google.com') && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Connect your Google account for easier sign-in
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<GoogleIcon />}
                        onClick={handleGoogleConnect}
                        fullWidth
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Connect Google Account
                      </Button>
                    </Box>
                  )}
                  
                  {user?.linkedAccounts?.some(acc => acc.providerId === 'google.com') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                      <Avatar sx={{ bgcolor: '#4285f4', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <GoogleIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Google Account</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Connected</Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>

                {/* Security */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SecurityIcon color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Security
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={async () => {
                        if (!user) return;
                        try {
                          const authService = AuthService.getInstance();
                          await authService.sendPasswordReset(user.email);
                          setSuccess('Password reset email sent! Please check your inbox.');
                        } catch (err) {
                          setError('Failed to send password reset email');
                        }
                      }}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Reset Password
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </CustomTabPanel>
        </ProfileSection>
      </ProfileContainer>
      
      <Footer />
      
      {/* Add/Edit Slot Dialog */}
      <Dialog open={slotDialogOpen} onClose={handleCloseSlotDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {editingSlot ? 'Edit Availability Slot' : 'Add New Availability Slot'}
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1, sm: 2 } }}>
          {slotDialogError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {slotDialogError}
            </Alert>
          )}
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Time"
              value={slotForm.startTime}
              onChange={(newValue) => newValue && setSlotForm({ ...slotForm, startTime: newValue })}
              sx={{ 
                width: '100%', 
                mt: 2,
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }
              }}
            />
          </LocalizationProvider>
          
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Duration</InputLabel>
            <Select
              value={slotForm.duration}
              onChange={(e: SelectChangeEvent<number>) => 
                setSlotForm({ ...slotForm, duration: Number(e.target.value) })
              }
              label="Duration"
              sx={{ 
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }
              }}
            >
              <MenuItem value={30} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>30 minutes</MenuItem>
              <MenuItem value={60} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>1 hour</MenuItem>
              <MenuItem value={90} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>1.5 hours</MenuItem>
              <MenuItem value={120} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>2 hours</MenuItem>
              <MenuItem value={150} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>2.5 hours</MenuItem>
              <MenuItem value={180} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>3 hours</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 2 }}>
            <CourtSearchSelect
              value={slotForm.courtId}
              onChange={(value, court) => setSlotForm({ ...slotForm, courtId: value, courtData: court || null })}
              label="Court"
              size="medium"
              fullWidth
              includeAnyOption
              anyOptionLabel="Any Court"
            />
          </Box>
          
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Visibility</InputLabel>
            <Select
              value={slotForm.visibility}
              onChange={(e: SelectChangeEvent<'public' | 'private'>) => 
                setSlotForm({ ...slotForm, visibility: e.target.value as 'public' | 'private' })
              }
              label="Visibility"
              sx={{ 
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }
              }}
            >
              <MenuItem value="public" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon fontSize="small" />
                  Public (Visible to all players)
                </Box>
              </MenuItem>
              <MenuItem value="private" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockIcon fontSize="small" />
                  Private (Only via shared link)
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
          <Button onClick={handleCloseSlotDialog} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Cancel
          </Button>
          <Button onClick={handleSaveSlot} variant="contained" color="primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {editingSlot ? 'Update' : 'Create'} Slot
          </Button>
        </DialogActions>
      </Dialog>
      
{/* Share Calendar Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Share Your Schedule
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1, sm: 2 } }}>
          {shareDialogSuccess && (
            <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {shareDialogSuccess}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Share your schedule with other players so they can see your availability and challenge you for matches.
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              value={`${window.location.origin}/players/${user?.uid}/schedule`}
              InputProps={{
                readOnly: true,
                sx: {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyShareLink}
              sx={{ 
                flexShrink: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: 'nowrap',
                borderColor: '#84b817',
                color: '#84b817',
                '&:hover': {
                  borderColor: '#6a9312',
                  backgroundColor: 'rgba(132, 184, 23, 0.04)',
                }
              }}
            >
              Copy
            </Button>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Share via
            </Typography>
          </Divider>

          {/* Mobile Messengers - Icon Only with Official Logos */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            gap: 2,
            mb: 2,
            justifyContent: 'center',
          }}>
            <Tooltip title="WhatsApp">
              <IconButton
                sx={{ 
                  width: 56,
                  height: 56,
                  bgcolor: '#25D366',
                  color: 'white',
                  '&:hover': { 
                    bgcolor: '#1da851',
                  },
                }}
                onClick={() => {
                  const shareUrl = `${window.location.origin}/players/${user?.uid}/schedule`;
                  const text = `Check out my tennis schedule and challenge me for a match!`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`, '_blank');
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </IconButton>
            </Tooltip>
            <Tooltip title="Telegram">
              <IconButton
                sx={{ 
                  width: 56,
                  height: 56,
                  bgcolor: '#0088cc',
                  color: 'white',
                  '&:hover': { 
                    bgcolor: '#006699',
                  },
                }}
                onClick={() => {
                  const shareUrl = `${window.location.origin}/players/${user?.uid}/schedule`;
                  const text = `Check out my tennis schedule and challenge me for a match!`;
                  window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`, '_blank');
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </IconButton>
            </Tooltip>
            <Tooltip title="Messenger">
              <IconButton
                sx={{ 
                  width: 56,
                  height: 56,
                  bgcolor: '#0084ff',
                  color: 'white',
                  '&:hover': { 
                    bgcolor: '#0066cc',
                  },
                }}
                onClick={() => {
                  const shareUrl = `${window.location.origin}/players/${user?.uid}/schedule`;
                  window.open(`fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`, '_blank');
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
                </svg>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Desktop Sharing Options */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            gap: 1,
            mb: 2,
            flexWrap: 'wrap',
          }}>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              sx={{ 
                fontSize: '0.875rem',
                borderColor: '#84b817',
                color: '#84b817',
                '&:hover': {
                  borderColor: '#6a9312',
                  backgroundColor: 'rgba(132, 184, 23, 0.04)',
                }
              }}
              onClick={() => {
                const shareUrl = `${window.location.origin}/players/${user?.uid}/schedule`;
                const subject = 'Tennis Match Schedule';
                const body = `Check out my tennis schedule and challenge me for a match!\n\n${shareUrl}`;
                window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              }}
            >
              Email
            </Button>
            {navigator.share && (
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                sx={{ 
                  fontSize: '0.875rem',
                  borderColor: '#84b817',
                  color: '#84b817',
                  '&:hover': {
                    borderColor: '#6a9312',
                    backgroundColor: 'rgba(132, 184, 23, 0.04)',
                  }
                }}
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/players/${user?.uid}/schedule`;
                  try {
                    await navigator.share({
                      title: 'Tennis Match Schedule',
                      text: 'Check out my tennis schedule and challenge me for a match!',
                      url: shareUrl,
                    });
                  } catch (err) {
                    // User cancelled or error occurred
                    console.log('Share cancelled or failed:', err);
                  }
                }}
              >
                More Options
              </Button>
            )}
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Note: Only public slots will be visible to other players. Private slots can only be seen via this direct link.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={handleCloseShareDialog} 
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: '#84b817',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Accept Challenge Confirmation Dialog */}
      <Dialog open={acceptDialogOpen} onClose={handleCancelAcceptChallenge} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Accept Challenge
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1, sm: 2 } }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to accept this challenge?
          </Typography>
          
          {challengeToAccept && (
            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Challenge Details:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>From:</strong> {getChallengerName(challengeToAccept)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Time:</strong> {formatDate(challengeToAccept.startTime)} at {formatTimeRange(
                  challengeToAccept.startTime,
                  challengeToAccept.endTime
                )}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Court:</strong> {challengeToAccept.courtName}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Message:</strong> "{challengeToAccept.message}"
              </Typography>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              <strong>Note:</strong> Accepting this challenge will automatically withdraw any other pending challenges for overlapping time slots and create a chat for coordination.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
          <Button onClick={handleCancelAcceptChallenge} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAcceptChallenge} variant="contained" color="success" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Accept Challenge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Challenge Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Cancel Match
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1, sm: 2 } }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to cancel this match?
          </Typography>
          
          {challengeToCancel && (
            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Match Details:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>With:</strong> {challengeToCancel.fromUserId === user?.uid 
                  ? challengeToCancel.toUserName 
                  : challengeToCancel.fromUserName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Time:</strong> {formatDate(challengeToCancel.startTime)} at {formatTimeRange(
                  challengeToCancel.startTime,
                  challengeToCancel.endTime
                )}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Court:</strong> {challengeToCancel.courtName}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Please provide a cancellation message that will be sent to your match partner:
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            value={cancellationMessage}
            onChange={(e) => setCancellationMessage(e.target.value)}
            placeholder="Enter your cancellation message..."
            variant="outlined"
            inputProps={{ maxLength: 500 }}
            helperText={`${cancellationMessage.length}/500 characters`}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
              '& .MuiFormHelperText-root': {
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }
            }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              <strong>Note:</strong> This message will be sent to the chat and via email to your match partner. The time slot will become available again for other players.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
          <Button onClick={handleCloseCancelDialog} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Keep Match
          </Button>
          <Button 
            onClick={handleConfirmCancellation} 
            variant="contained" 
            color="error"
            disabled={!cancellationMessage.trim()}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel Match
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerProfilePage;